import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { useKurs } from '@/hooks/useKurs';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const KATEGORI_LIST = ['Stiker', 'Box', 'Sablon', 'Hot Print', 'Gift Card', 'Coloring', 'Lainnya'];

export default function ItemTambahanForm({ item, kategoriAwal, onClose, onSaved, hidePrices = false }) {
  const qc = useQueryClient();
  const isEdit = !!item?.id;
  const { kurs, kursEuro } = useKurs();

  // Fetch existing ItemTambahan to build vendor list per kategori
  const { data: allItems = [] } = useQuery({
    queryKey: ['item-tambahan-all'],
    queryFn: () => base44.entities.ItemTambahan.list(),
  });

  const vendorsByKategori = useMemo(() => {
    const map = {};
    KATEGORI_LIST.forEach(k => { map[k] = new Set(); });
    allItems.forEach(b => {
      if (b.vendor && b.kategori && !b.dari_client) {
        if (map[b.kategori]) map[b.kategori].add(b.vendor.trim());
      }
    });
    const result = {};
    Object.entries(map).forEach(([k, s]) => {
      result[k] = Array.from(s).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
    });
    return result;
  }, [allItems]);

  const [form, setForm] = useState({
    nama: item?.nama || '',
    kategori: item?.kategori || kategoriAwal || 'Stiker',
    brand: item?.brand || '',
    harga_rupiah: item?.harga_rupiah || '',
    harga_jual: item?.harga_jual || '',
    harga_dollar: item?.harga_dollar || '',
    harga_euro: item?.harga_euro || '',
    mata_uang: item?.mata_uang || (item?.harga_euro ? 'EUR' : 'USD'),
    stok: item?.stok ?? 0,
    vendor: item?.vendor || '',
    catatan: item?.catatan || '',
    status: item?.status ?? 'aktif',
    dari_client: item?.dari_client || false,
    client_owner: item?.client_owner || '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // USD → Rupiah otomatis
  useEffect(() => {
    if (form.mata_uang === 'USD' && form._lastEdited === 'valas' && form.harga_dollar && kurs) {
      setForm(p => ({ ...p, harga_rupiah: (parseFloat(p.harga_dollar) * kurs).toFixed(0) }));
    }
  }, [form.harga_dollar, form.mata_uang, kurs]);

  // EUR → Rupiah otomatis
  useEffect(() => {
    if (form.mata_uang === 'EUR' && form._lastEdited === 'valas' && form.harga_euro && kursEuro) {
      setForm(p => ({ ...p, harga_rupiah: (parseFloat(p.harga_euro) * kursEuro).toFixed(0) }));
    }
  }, [form.harga_euro, form.mata_uang, kursEuro]);

  // Rupiah → USD/EUR otomatis
  useEffect(() => {
    if (form._lastEdited === 'rupiah' && form.harga_rupiah) {
      if (form.mata_uang === 'USD' && kurs) {
        setForm(p => ({ ...p, harga_dollar: (parseFloat(p.harga_rupiah) / kurs).toFixed(4) }));
      } else if (form.mata_uang === 'EUR' && kursEuro) {
        setForm(p => ({ ...p, harga_euro: (parseFloat(p.harga_rupiah) / kursEuro).toFixed(4) }));
      }
    }
  }, [form.harga_rupiah, form.mata_uang, kurs, kursEuro]);

  const saveMut = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        await base44.entities.ItemTambahan.update(item.id, data);
        if (item.nama && data.nama && item.nama !== data.nama) {
          try {
            const dbList = await base44.entities.DatabaseBahan.list();
            for (const db of dbList) {
              if (db.item_tambahan && Array.isArray(db.item_tambahan)) {
                let changed = false;
                db.item_tambahan.forEach(it => {
                  if (it.nama === item.nama) {
                    it.nama = data.nama;
                    changed = true;
                  }
                });
                if (changed) {
                  await base44.entities.DatabaseBahan.update(db.id, { item_tambahan: db.item_tambahan });
                }
              }
            }
          } catch (err) { console.error('Failed to cascade update Item Tambahan in DatabaseBahan', err); }
        }
      } else {
        await base44.entities.ItemTambahan.create(data);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-tambahan'] });
      onSaved?.();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMut.mutate({
      ...form,
      harga_rupiah: Number(form.harga_rupiah) || 0,
      harga_jual: form.harga_jual ? parseFloat(form.harga_jual) : null,
      harga_dollar: form.mata_uang === 'USD' && form.harga_dollar ? parseFloat(form.harga_dollar) : null,
      harga_euro: form.mata_uang === 'EUR' && form.harga_euro ? parseFloat(form.harga_euro) : null,
      mata_uang: form.mata_uang,
      stok: Number(form.stok) || 0,
      status: form.status,
      dari_client: form.dari_client,
      client_owner: form.dari_client ? form.client_owner : '',
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edit' : 'Tambah'} Item Tambahan</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Kepemilikan Barang</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => set('dari_client', false)} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${!form.dari_client ? 'bg-accent text-accent-foreground border-accent shadow-sm' : 'border-border text-muted-foreground hover:border-accent/50'}`}>Inventori Satala</button>
              <button type="button" onClick={() => { setForm(p => ({ ...p, dari_client: true, harga_rupiah: 0, harga_jual: 0, harga_dollar: '', harga_euro: '' })); }} className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.dari_client ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm' : 'border-border text-muted-foreground hover:border-emerald-500/50'}`}>Titipan Client</button>
            </div>
          </div>

          {form.dari_client && (
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 mt-2">
              <label className="text-xs font-bold text-emerald-800 mb-1 block">Nama Client / Brand Titipan *</label>
              <Input 
                value={form.client_owner || ''} 
                onChange={e => setForm(p => ({...p, client_owner: e.target.value}))} 
                placeholder="Misal: Dermatech, atau Klinik Bunga..." 
                className="border-emerald-200 focus-visible:ring-emerald-500 bg-white"
              />
              <p className="text-[10px] text-emerald-600 mt-1">Isi dengan nama brand klien secara persis agar tercatat di Data Klien mereka.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Nama Item *</label>
            <Input value={form.nama} onChange={e => set('nama', e.target.value)} placeholder="Misal: Stiker Logo 5x5cm" required />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Kategori *</label>
            <div className="flex flex-wrap gap-2">
              {KATEGORI_LIST.map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set('kategori', k)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${form.kategori === k ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Brand</label>
            <Input value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="Nama brand (misal: Cielmora)" />
          </div>

          {/* Currency selector */}
          {!form.dari_client && !hidePrices && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Acuan Kurs Mata Uang Valas *</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'USD', _lastEdited: 'rupiah' }));
                  }}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                    form.mata_uang === 'USD'
                      ? 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm'
                      : 'border-border text-muted-foreground hover:border-accent/50'
                  }`}
                >
                  USD ($)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, mata_uang: 'EUR', _lastEdited: 'rupiah' }));
                  }}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-semibold transition-all ${
                    form.mata_uang === 'EUR'
                      ? 'bg-blue-50 text-blue-700 border-blue-300 shadow-sm'
                      : 'border-border text-muted-foreground hover:border-accent/50'
                  }`}
                >
                  EUR (€)
                </button>
              </div>
            </div>
          )}

          {/* Harga */}
          {!form.dari_client && !hidePrices && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {form.mata_uang === 'USD' ? (
                    <>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Beli USD ($)</label>
                      <Input type="number" step="0.01" value={form.harga_dollar} onChange={e => setForm(p => ({ ...p, harga_dollar: e.target.value, _lastEdited: 'valas' }))} placeholder="0.00" />
                    </>
                  ) : (
                    <>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Beli EUR (€)</label>
                      <Input type="number" step="0.01" value={form.harga_euro} onChange={e => setForm(p => ({ ...p, harga_euro: e.target.value, _lastEdited: 'valas' }))} placeholder="0.00" />
                    </>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Beli Rupiah (Rp) *</label>
                  <Input type="number" value={form.harga_rupiah} onChange={e => setForm(p => ({ ...p, harga_rupiah: e.target.value, _lastEdited: 'rupiah' }))} placeholder="0" required />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Harga Jual Rupiah (Rp)</label>
                <Input type="number" value={form.harga_jual} onChange={e => set('harga_jual', e.target.value)} placeholder="Opsional (boleh dikosongkan jika belum tahu)" />
              </div>

              {form.mata_uang === 'USD' && form.harga_dollar && form.harga_rupiah && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  💱 ${parseFloat(form.harga_dollar).toFixed(2)} × Rp {kurs.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = Rp {(parseFloat(form.harga_dollar) * kurs).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
              {form.mata_uang === 'EUR' && form.harga_euro && form.harga_rupiah && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  💱 €{parseFloat(form.harga_euro).toFixed(2)} × Rp {kursEuro.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = Rp {(parseFloat(form.harga_euro) * kursEuro).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              )}
            </>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Stok (pcs)</label>
            <Input type="number" min="0" value={form.stok} onChange={e => set('stok', e.target.value)} placeholder="0" />
          </div>

          {!form.dari_client && (
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Vendor / Supplier</label>
            <SearchableCombobox
              value={form.vendor}
              onValueChange={v => set('vendor', v)}
              options={vendorsByKategori[form.kategori] || []}
              placeholder="Pilih atau ketik vendor baru..."
              searchPlaceholder="Cari vendor..."
              allowCustom={true}
            />
            {(vendorsByKategori[form.kategori] || []).length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {(vendorsByKategori[form.kategori] || []).length} vendor tersedia untuk kategori <strong>{form.kategori}</strong>
              </p>
            )}
          </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan</label>
            <Input value={form.catatan} onChange={e => set('catatan', e.target.value)} placeholder="Catatan tambahan (opsional)" />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Status Item *</label>
            <div className="flex gap-2">
              {[
                { v: 'aktif', l: 'Aktif' },
                { v: 'pasif', l: 'Pasif' },
                { v: 'nonaktif', l: 'Nonaktif' },
              ].map(st => (
                <button key={st.v} type="button" onClick={() => set('status', st.v)}
                  className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.status === st.v ? 'bg-primary text-primary-foreground border-primary shadow-sm' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                >
                  {st.l}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button type="submit" disabled={saveMut.isPending || !form.nama.trim()} className="flex-1">
              {saveMut.isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Item'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}